import React, { useEffect, useState } from 'react';
import { trainingProgress, trainingProgressKey } from '../../utils/trainingProgress';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import FirmMatchPrompt from '../../components/doorlypro/FirmMatchPrompt';
import ReadinessChecklist from '../../components/doorlypro/ReadinessChecklist';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import { Icon } from '../../utils/icons';
import { RequirePartnerAuth } from '../../components/DoorlyProLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const inputStyle = {
  width: '100%',
  height: 48,
  padding: '0 14px',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  fontSize: 14,
  color: '#111827',
  backgroundColor: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const focusBorder = (e) => (e.target.style.borderColor = '#3a6ad9');
const blurBorder = (e) => (e.target.style.borderColor = '#e5e7eb');

// Agreement + Banking are no longer onboarding steps — they're completed as
// Dashboard tasks after onboarding (required before the first fee payout). So
// every partner type now follows Profile → Training → Complete.
const STEPS = [
  { key: 'email_verified', label: 'Profile', num: 1 },
  { key: 'training_pending', label: 'Training', num: 2 },
  { key: 'active', label: 'Complete', num: 3 },
];

// ─────────────────────────────────────────────────────────────────────────────
// ⚠️ THE DECK A PARTNER COMPLETES TO ACTIVATE THEIR ACCOUNT, so what it omits is
// what they were never told. It is the last onboarding gate — finishing it flips
// the account to active — which makes it the one place every partner is
// guaranteed to have read.
//
// 🚨 IT MUST TRACK THE AGREEMENT THEY SIGNED, NOT WHERE THE BUSINESS IS GOING.
// This is the opposite treatment from the employee manual, deliberately: an
// employee needs to know the intended direction, a partner is being told what
// they owe. Telling a partner they owe a duty their executed document does not
// contain is the failure this is shaped to avoid. Anything forward-looking must
// be visibly marked as coming, never stated as a current duty.
//
// ⚠️ ONE DECK PER TYPE. Realtors and loan officers shared a single deck written
// in realtor language — "your client", "send buyers" — so a loan officer was told
// their role was to send buyers when their role is to submit an application, and
// both were shown compensation copy that could only describe one basis.
// Homebuilders already had their own; these are the other two.
// ─────────────────────────────────────────────────────────────────────────────

// Identical for every type: who Doorly is for. Shared rather than duplicated so a
// change to the credit box cannot land in one deck and not the other.
// ─────────────────────────────────────────────────────────────────────────────
// THE ACCOUNT-LEVEL PREAMBLE — PREPENDED, NOT A FOURTH DECK.
//
// 🔑 THE HIGHER-LEVEL ROLE NEEDS DIFFERENT COPY, NOT DIFFERENT CONTENT. A
// mortgage broker still needs to know what Doorly is, who qualifies and where
// the line sits — that is the type deck, unchanged. What they need FIRST is what
// being the account means, and that is this one module.
//
// ⚠️ A FOURTH SET OF DECKS WOULD HAVE MEANT NINE ARRAYS FOR THREE PRODUCTS. The
// realtor, loan-officer and homebuilder decks already carry the type content; a
// broker-flavoured copy of each would drift from its sibling the first time
// either was edited, which is this codebase's most repeated failure. One module
// in front of the existing three keeps the type content single-sourced.
const ACCOUNT_LEVEL_MODULE = {
  id: 'account_level', iconName: 'building', title: 'Your Firm on Doorly',
  color: '#111827', textColor: '#ffffff', duration: '3 min',
  slides: [
    // 🔑 THE ONE FACT EVERYTHING ELSE FOLLOWS FROM.
    { heading: 'Licensing and payment run through your firm', body: 'Your people work under your license, and Doorly pays the firm rather than the individual. That is why your firm has its own account here and not just a set of logins: the account is who Doorly contracts with, holds the payout details, and is the party your agents\' agreements are made against.', visualIcon: 'building' },

    // ⚠️ THE THING THEY WILL OTHERWISE ASSUME IS THEIR JOB.
    { heading: 'Your people each sign their own agreement', body: 'Every agent or loan officer at your firm signs their own agreement with Doorly. You do not sign on their behalf and there is nothing for you to sign for them.\n\nWhat you do is counter-sign — your signature is the second one on each of their agreements, because the license they work under and the payment they earn are both yours.', visualIcon: 'document' },

    // 🚨 THE REASON, STATED, BECAUSE A SIGNATURE REQUEST WITH NO REASON GETS
    // DEFERRED — and this one has a consequence for their own people.
    { heading: 'Why your signature matters beyond the paperwork', body: 'It is also what lets Doorly bring you in if one of your agents becomes unreachable to a buyer. We cannot send your firm that notice unless your firm has agreed to receive it.\n\nSo a counter-signature you have not got round to is not just an open task — it is a buyer we would have no way to escalate to you about.', visualIcon: 'warning' },

    // ⚠️ AND WHAT IS *NOT* BLOCKED, because a broker who thinks their people are
    // stuck will chase this over things that actually are.
    { heading: 'Nothing of theirs waits on you', body: 'Your agents can register, submit buyers and be owed their fees before you have counter-signed. Their work is not held up by your signature, and neither are their earnings.\n\nYou will see whose agreements are waiting on you under Documents, with the oldest first.', visualIcon: 'check-circle' },

    // 🔑 THE RARE CASE, AND WHY IT CHANGES WHAT THEY SIGN.
    { heading: 'If you bring a buyer in yourself', body: 'Most brokers do not — your people generate the business and submit it. But if you do submit your own buyer or loan file, you are doing the work the agreement covers, so you sign one of your own as well as counter-signing theirs.\n\nDoorly asks for it at that point rather than up front, because until you submit something there is nothing for you to be a party to.', visualIcon: 'search' },
  ],
};

const IDEAL_BUYER_MODULE = {
  id: 'ideal_client',
  iconName: 'search',
  title: 'Ideal Buyer',
  color: '#3a6ad9',
  textColor: '#ffffff',
  duration: '3 min',
  slides: [
    { heading: 'Who is the right fit for Doorly?', body: 'Doorly is designed for buyers who are close to qualifying—or frustrated with traditional lenders. This includes self-employed borrowers, business owners, credit-challenged buyers, and anyone with non-traditional income or documentation challenges.', visualIcon: 'user' },
    { heading: 'Green flags to look for', body: 'Stable income (including self-employed or non-traditional)\nHas funds for down payment and closing costs\nMotivated to buy in the near term\nOpen to a streamlined, digital process', visualIcon: 'check-circle' },
    { heading: 'Red flags to avoid', body: 'No verifiable income\nActive bankruptcy or unresolved major credit issues\nInsufficient funds to close\nNon-owner-occupied / investment intent\nA refinance — Doorly finances purchases only', visualIcon: 'warning' },
  ],
};

const WHAT_IS_DOORLY_SLIDES = [
  { heading: 'Doorly is a new way to buy a home', body: 'Doorly is a full-stack homebuying platform that takes buyers from application to keys—all in one place. We help modern buyers get approved, make strong offers, and close on a home faster than traditional financing.', visualIcon: 'home' },
  { heading: 'How it works in 3 steps', body: '1. The buyer applies and gets approved through Doorly\n2. The buyer selects the home they want directly in-app\n3. Doorly purchases the home with a cash-backed offer and simultaneously resells the home to the buyer with a structured mortgage (Doorly\'s Ability-First Mortgage)', visualIcon: 'arrow' },
];

// ⚠️ THE COMPENSATION SLIDES NAME NO FIGURE, NO BASIS AND NO TIMING. The
// agreement is the authoritative record and the only place a partner should read
// terms from — a deck that paraphrases them creates a second version that can
// disagree with the signed one.
const REALTOR_TRAINING_MODULES = [
  {
    id: 'what_is_doorly', iconName: 'home', title: 'What is Doorly?',
    color: '#acf44e', textColor: '#1a1a1a', duration: '2 min',
    slides: [
      ...WHAT_IS_DOORLY_SLIDES,
      { heading: 'Your role as a DoorlyPro partner', body: 'You submit a buyer to Doorly, and you do what you already do for them: find properties, show homes, and help them choose one. They request the offer on it from their own Doorly account, Doorly buys the home and resells it to them with financing they could not get elsewhere. When it closes, you are paid for the services you performed.', visualIcon: 'users' },
    ],
  },
  IDEAL_BUYER_MODULE,
  {
    // ⚠️ THE SERVICES ARE THE JOB, AND THEY ARE WHY THE FEE IS PAYABLE. The
    // agreement pays for buyer-side real estate services actually performed, so a
    // partner who does not know what those services are cannot know what they are
    // being paid for or when they have earned it.
    id: 'your_services', iconName: 'search', title: 'Your Services',
    color: '#7C58F5', textColor: '#ffffff', duration: '3 min',
    slides: [
      { heading: 'What you are engaged to do', body: 'For each buyer you bring to Doorly:\n\nIdentify properties that fit their criteria and budget\nArrange and conduct showings\nAdvise them on choosing a specific home, using your market knowledge\nSubmit the buyer through DoorlyPro, then point them to their own account to request the offer\nKeep your information accurate, and correct anything you learn is wrong\nStay reachable to the buyer and to Doorly\nDirect all financing questions to Doorly', visualIcon: 'check-circle' },
      { heading: 'Doorly does not do the house-hunting', body: 'Doorly does not identify properties or advise buyers on which home to choose. That is your work, and it is the reason the agreement exists. Doorly handles the offer, the negotiation, the inspection and the closing — you are not involved in writing the Doorly offer and you do not negotiate on Doorly\'s behalf.', visualIcon: 'home' },
      // 🚨 THE ONLY DOLLAR FIGURE A PARTNER EVER SEES, AND IT IS THE BUYER'S TO
      // GIVE. Added because the feature went live and nothing told partners it
      // existed — the first they would have known was a badge appearing on the
      // dashboard with no explanation of what "Range not shared" meant.
      //
      // ⚠️ IT SAYS THE BUYER CONTROLS IT, TWICE, AND NAMES THE DECLINE. A
      // partner who reads "not shared" as a system problem will raise a ticket
      // or lean on Doorly; the person who can change the answer is the buyer.
      { heading: 'Your buyer can share their shopping range', body: 'On their application your buyer is asked whether Doorly may tell you the maximum home price they are approved for, rounded down. If they say yes, it shows on your dashboard as "Up to $X" once they are approved.\n\nThat is the only figure you will ever see. Not their income, credit, payment or cash to close — you do not see their file, and neither does anyone else outside Doorly.', visualIcon: 'currency' },
      { heading: 'If it says "Range not shared"', body: 'Two different things look the same on the dashboard, and the tooltip tells you which.\n\nYour buyer has not answered yet — ask them to share it from their Doorly account. Or they were asked and chose not to — then talk to them about budget directly, the way you would with any other client.\n\nEither way it is their call and they can change it at any time. Doorly cannot override it, so there is nothing to ask us for.', visualIcon: 'lock' },
      { heading: 'The standard you are held to', body: 'Perform these services with the care and diligence customary for a licensed real estate professional representing a buyer in your market. Nothing more exotic than that — it is the job you already do.', visualIcon: 'document' },
    ],
  },
  {
    // ⚠️ id RENAMED FROM how_to_refer. "Refer" is introduce-only vocabulary from
    // the agreement this deck no longer teaches, sitting on the module whose
    // whole point is that a submission is a buyer AND a home. Safe to rename:
    // only the boolean partner_profiles.training_completed is persisted
    // server-side, so module ids live in component state and changing one
    // cannot strand a partner mid-deck. The LO deck keeps how_to_refer, because
    // for a loan officer the referral really is the arrangement.
    id: 'how_to_submit', iconName: 'send', title: 'How to Submit',
    color: '#ff082f', textColor: '#ffffff', duration: '2 min',
    slides: [
      // 🚨 REWRITTEN FOR v2.1, WHICH REVERSED v2.0 §1.4. This slide used to say
      // "A submission is a buyer AND a home" and instruct the partner to wait
      // until their buyer had picked a house. That gated the whole relationship
      // on home selection happening FIRST, which is backwards for how people
      // buy and for a product that evaluates the buyer first.
      //
      // ⚠️ THE RISK IN THE NEW WORDING IS THE OPPOSITE ONE: "just submit the
      // buyer" read alone sounds like the job ends at submission. It does not —
      // §2.3 says the role BEGINS at Submission — so the slide carries the whole
      // arc rather than only the part that changed.
      { heading: 'Submit the buyer as soon as you have one', body: 'A submission is a buyer. That is all it needs to be — you do not wait for them to choose a house first.\n\nMost buyers want to know what they can afford before they start looking, so submitting early is usually the right order: submit, we evaluate them, and then you go shopping with a number you can both work to.', visualIcon: 'arrow' },
      // 🔑 THE MISREADING THIS SLIDE EXISTS TO PREVENT. A partner who thinks
      // submitting IS the job stops working the buyer, and §4.2 conditions the
      // fee on Home Selection being completed before Doorly offers.
      { heading: 'Submitting starts your work — it does not finish it', body: 'Your agreement calls the submission "the beginning of your engagement for that buyer, not the end of it."\n\nAfter you submit, you keep doing what you already do: find properties, run showings, and advise them on choosing a home. Then point them to their Doorly account to request the offer on it.\n\nThat home-selection work is what the fee is for. A buyer you submitted and then left alone has not been given the services you are paid for.', visualIcon: 'search' },
      { heading: 'Your buyer applies themselves', body: 'Your buyer creates their own Doorly account and completes the loan application themselves. You do not collect their income, assets or documents — and under your agreement you must not. Doorly underwrites the file and provides all final terms.', visualIcon: 'mail' },
      // ⚠️ THE APP IS BORROWER-DRIVEN AT THIS STEP AND THE DECK USED TO SAY
      // OTHERWISE. It told realtors to tell Doorly which house was chosen —
      // there is no route in DoorlyPro to do that. The buyer requests the offer
      // themselves from their own account, which is also what triggers the
      // "your buyer found a home" email to the partner.
      //
      // ⚠️ THE AGREEMENT SAYS SOMETHING THIS PRODUCT DOES NOT DO. v2.1 §2.1
      // obliges the Broker to "notify Doorly, through DoorlyPro, of the property
      // the Buyer has selected" and no such mechanism exists. Recorded in
      // engineeringFollowUps as v21-broker-notifies-selected-property. The deck
      // describes what actually happens, because teaching a partner to use a
      // button that is not there helps nobody.
      { heading: 'Your buyer requests the offer, from their own account', body: 'Once the two of you have settled on a house, point your buyer to Homes in their Doorly account — they find that listing and request the offer themselves. There is no button on your side and nothing for you to send us.\n\nSending them there is your part of it, and it is in your agreement. We email you as soon as they request it, so you know it landed.', visualIcon: 'home' },
      { heading: 'Home selection can come before or after you submit', body: 'Either order is fine. You can submit a buyer and then go shopping with them, or submit someone who has already found a house.\n\nWhat matters is that the home-selection work is yours, it happens externally of the Doorly app, and is done before the buyer can advance forward to purchase a home.', visualIcon: 'check-circle' },
      { heading: 'Full visibility, no guesswork', body: 'Track your buyer\'s progress in real time—from application to closing—directly in your dashboard. You see where the file is, not what is in it.', visualIcon: 'chart' },
    ],
  },
  {
    // 🔑 THIS MODULE HAS TEETH AND IS THE ONE MOST LIKELY TO BE SKIMMED. Going
    // quiet has a defined consequence — Doorly takes over the buyer and the fee
    // for that buyer is not payable — so the sequence and the clock are stated
    // plainly rather than softened.
    id: 'staying_available', iconName: 'clock', title: 'Staying Available',
    color: '#111827', textColor: '#ffffff', duration: '2 min',
    slides: [
      { heading: 'Your buyer depends on you being reachable', body: 'A buyer cannot move forward if they cannot reach their agent. Your agreement asks you to stay reasonably available to the buyer and to Doorly while their file is live.', visualIcon: 'users' },
      { heading: 'If a buyer tells us they cannot reach you', body: 'Doorly emails you at the address on your DoorlyPro account and records when it was delivered. You have two business days to respond — either acknowledge it in DoorlyPro, or show that you have been in contact with the buyer.\n\nIf that passes, you get a second notice and another two business days.\n\nBusiness days are Monday to Friday, not counting federal holidays. The clock starts when the email reaches you, not when we send it.', visualIcon: 'mail' },
      { heading: 'What happens if both pass', body: 'Doorly may take over the buyer-side work for that buyer, and the fee for that buyer is not payable. It affects that buyer only — it does not end your agreement or affect anyone else you have submitted.\n\nKeep the email address on your DoorlyPro profile current. It is where these notices go.', visualIcon: 'warning' },
      // \u26a0\ufe0f THE HALF OF \u00a72.5 THAT PROTECTS THE PARTNER, AND IT IS NOT A COURTESY.
      // Doorly may not exercise the clause where its notices were not delivered,
      // so this is a limit on Doorly rather than a concession to the partner.
      // Telling them costs nothing and makes the process visibly fair rather
      // than merely firm \u2014 and it is now true in code, not only on paper: a
      // bounced notice can never start the clock (utils/partnerNotices.js).
      { heading: 'If the email never reached you, none of this applies', body: 'Doorly cannot take a buyer from you on notices that were not delivered. If an email bounced, or you can show that not hearing from us is why you did not respond, the clock does not run and the fee is not affected.\n\nThat is why keeping your email current matters, and it is why we record delivery rather than just sending.', visualIcon: 'check-circle' },
    ],
  },
  {
    id: 'compensation', iconName: 'currency', title: 'How You Get Paid',
    color: '#fffd5b', textColor: '#1a1a1a', duration: '2 min',
    slides: [
      { heading: 'You earn when your buyer closes', body: 'You are paid for the services you actually performed, when the purchase and resale close. The amount, how it is calculated, and when it is paid are set out in your agreement—that document is the authoritative record.', visualIcon: 'currency' },
      // ⚠️ §4.2 HAS TWO TIMING CONDITIONS SINCE v2.1, NOT ONE. It used to read
      // "you submitted the buyer and the selected property, before that buyer
      // contacted Doorly independently" — a single condition, because a
      // Submission carried the property. Now the Submission and the Home
      // Selection are separate acts with separate deadlines, and a partner told
      // only about the first can satisfy it and still not be paid.
      { heading: 'What has to be true to be paid', body: 'You performed the services\nYou submitted the buyer before they contacted Doorly on their own\nYou completed home selection before Doorly made an offer\nThe transaction closed\nYour agreement is signed and current\nDoorly has your W-9\nYour license was active at submission and at closing', visualIcon: 'check-circle' },
      { heading: 'Nothing is owed on a file that does not close', body: 'A fee arises on a closing. A buyer who applies, or is approved, or goes under contract and does not close does not generate a fee—however far the file progressed. Your agreement sets out the payment terms and conditions in full.', visualIcon: 'chart' },
      // 🚨 THE CONDITION A PARTNER CANNOT CHECK FOR THEMSELVES, AND THEREFORE THE
      // ONE MOST LIKELY TO BECOME A DISPUTE. Every other condition is about what
      // the partner did. This one is about what DOORLY already knew, which is
      // invisible from outside — a realtor can perform every service, submit a
      // complete file and close it, and earn nothing because Doorly emailed that
      // buyer eleven months ago.
      //
      // ⚠️ SO IT IS WORDED AS AN ACTION, NOT A WARNING. "Ask before you do the
      // work" is the only thing a partner can actually do about it, and a slide
      // that states the rule without the remedy just tells them they may have
      // already lost.
      { heading: 'Check with us before you start, if we might already know them', body: 'A fee is not payable if the buyer was already in Doorly\'s system, or if Doorly had documented contact with them in the previous twelve months. That is true however much work you do afterwards.\n\nYou cannot see that from your side. So if there is any chance the buyer has spoken to Doorly, seen an ad, or started an application before — ask us first. We will tell you, and it takes a message.\n\nAsking before you invest weeks in a buyer is the whole point of this slide.', visualIcon: 'search' },
      { heading: 'What else stops a fee', body: 'No fee is payable if you provided misleading information, or engaged in prohibited conduct — including any RESPA violation or steering a buyer for your own benefit.\n\nAnd if Doorly has taken over the buyer-side work for a buyer because you could not be reached, the fee for that buyer is not payable. See Staying Available.', visualIcon: 'warning' },
    ],
  },
  {
    id: 'compliance', iconName: 'lock', title: 'Compliance',
    color: '#3a6ad9', textColor: '#ffffff', duration: '3 min',
    slides: [
      // 🚨 THE PROHIBITIONS. A partner could breach any of these without ever
      // having been told, and the deck they complete to activate is where they
      // are told.
      { heading: 'Do not touch the loan application', body: 'Your buyer completes their own application with Doorly. You may not take a loan application or fill in any part of one, and you may not collect, handle, evaluate or pass on their income, asset or credit documents. Taking income over the phone to "save them time" is the most common way this happens, and it is exactly what your agreement forbids.', visualIcon: 'warning' },
      { heading: 'Do not discuss loan terms', body: 'Do not quote, promise or discuss interest rates, loan terms, pricing or underwriting outcomes, and do not guarantee approval. Send financing questions to Doorly — they provide all final terms directly.', visualIcon: 'warning' },
      { heading: 'Never collect a fee from the buyer', body: 'You may not collect any fee, deposit or other consideration from a buyer in connection with your agreement or the AFM program. Your compensation comes from Doorly and from nowhere else. This is a RESPA matter, not a Doorly preference.', visualIcon: 'warning' },
      { heading: 'You do not represent Doorly', body: 'Doorly is the buyer in each transaction. You do not represent Doorly, do not hold yourself out as representing Doorly, and do not negotiate on Doorly\'s behalf. Represent the program accurately and follow Doorly\'s published guidelines.', visualIcon: 'document' },
      { heading: 'You\'re ready', body: 'You now know what services you perform, what a complete submission is, what keeps you available, and how you get paid. Start bringing buyers and homes, and let Doorly handle the financing.', visualIcon: 'check-circle' },
    ],
  },
];

// 🚨 REWRITTEN FOR THE WHOLESALE BROKER AGREEMENT v2.1 — NOT PATCHED, BECAUSE
// THE RELATIONSHIP CHANGED RATHER THAN THE WORDING.
//
// The v1.0 instrument was a REFERRAL agreement. Its §2.2 forbade the broker from
// collecting or evaluating income, asset or credit documents and from "any
// activity constituting mortgage origination on behalf of Doorly", and §2.1 said
// no financial documents should be uploaded directly. This deck taught that: an
// introduction, a borrower who applies to Doorly themselves, a partner who sees
// progress and nothing else. Every one of those slides is now wrong.
//
// 🔑 THE DISTINCTION THE WHOLE DOCUMENT TURNS ON, AND THE ONE THING A BROKER
// MUST LEAVE WITH: THEY ORIGINATE IN THEIR OWN CAPACITY, UNDER THEIR OWN
// LICENSE, FOR THE BORROWER — NOT AS DOORLY'S AGENT. "May originate" and "may
// act for Doorly" are opposite answers in this agreement (§2.2 first bullet, §8),
// and a broker who collapses them into "I work for Doorly now" has misunderstood
// the only thing that makes the arrangement lawful.
//
// ⚠️ NOTHING HERE SAYS WHEN THE LOAN ESTIMATE OR CLOSING DISCLOSURE ISSUES,
// DELIBERATELY. §2.3 makes the timing CONDITIONAL on what the broker had when
// they took the application, and Doorly's own trigger does not yet read that
// (see engineeringFollowUps wholesale-le-trigger-ignores-broker-application-date).
// A claim about when a disclosure arrives, on a file type where the answer
// depends on what the broker sent, is a promise Doorly may not keep.
//
// ⚠️ AND THE TONE IS DELIBERATELY WARMER THAN THE CONTRACT IT DESCRIBES. An
// earlier draft read like the agreement — accurate and cold. A broker choosing
// where to send a hard file is a colleague, not a counterparty being warned, and
// copy that reads as a list of ways to be in breach is copy people skim. The
// obligations are all still here; they are phrased as how the thing works.
const LOAN_OFFICER_TRAINING_MODULES = [
  {
    id: 'what_is_doorly', iconName: 'home', title: 'What is Doorly?',
    color: '#acf44e', textColor: '#1a1a1a', duration: '2 min',
    slides: [
      ...WHAT_IS_DOORLY_SLIDES,
      { heading: 'Doorly is your investor, not your employer', body: 'You take the application, gather the documents, counsel your borrower and send us a complete file. We underwrite it, price it, fund it, buy the home and resell it to your borrower.\n\nYou do all of that as a licensed broker working for your borrower — in your own capacity, under your own license. You are not originating on our behalf and you do not represent us. That distinction runs through the whole agreement, and it is what lets you do the work.', visualIcon: 'users' },
      // ⚠️ EXPANDED TO NAME THE NON-QM CASE. "Files you cannot place" reads as a
      // last resort; a bank-statement borrower whose buying power goes UP is a
      // first-choice reason to send a file here, and it is the one most likely to
      // be missed by a broker who already has non-QM outlets.
      { heading: 'Where Doorly fits in your book', body: 'The obvious files are the ones you cannot place through your existing lenders — self-employed borrowers, non-traditional income, credit events, documentation that will not fit an agency box.\n\nBut we are often a better answer for non-QM borrowers you CAN place. If you are working a bank-statement loan, compare the approval: we count more of that income than a typical bank-statement program does, which can mean a higher purchase price for the same borrower. Worth running the two side by side before you assume you already have the best home for it.\n\nThe credit box is the same for everyone — coming through you does not make a file easier to approve. What it does is make it possible to submit.', visualIcon: 'chart' },
    ],
  },
  IDEAL_BUYER_MODULE,
  {
    // 🚨 THE MODULE THAT DID NOT EXIST BEFORE. Under v1.0 there was nothing to
    // teach here — the broker introduced someone and stopped.
    id: 'origination_services', iconName: 'document', title: 'What You Do',
    color: '#7C58F5', textColor: '#ffffff', duration: '3 min',
    slides: [
      { heading: 'The work you are engaged to do', body: 'For each borrower:\n\n• Take a complete Uniform Residential Loan Application\n• Counsel them on the AFM Program and whether it suits them\n• Gather and review the income, asset and employment documentation our guidelines ask for\n• Get their written authorizations, including any we need to receive a credit report\n• Assemble and submit the loan file through DoorlyPro\n• Send anything else we ask for during underwriting, and let us know if you find something was wrong', visualIcon: 'check-circle' },
      { heading: 'That work is what you are paid for', body: 'Your fee is for origination services you actually performed, on a file that closes. It is not a finder\'s fee and it is not for an introduction.\n\nThat is the commercial logic and the compliance logic at the same time, which is a nice place to be. The amount, the basis and the timing are all in your agreement — that document is the authoritative record.', visualIcon: 'currency' },
      // ⚠️ THE WARRANTIES STAY, AND THE CLAWBACK SLIDE THAT FOLLOWED THEM IS
      // GONE. §2.6 is real and lives in the agreement; a whole slide on
      // indemnity and repayment-on-demand read as a threat in a deck whose job
      // is to get somebody started. The substance a broker acts on is what they
      // are attesting to, which is this slide.
      { heading: 'What you are confirming on every file', body: 'That you took the application yourself, in your own capacity and under your own license. That the file is true and complete so far as you know. That the documents came from the borrower or an independent source and none were altered. That the borrower\'s authorizations came before the documents they cover.\n\nAnd that no fee or anything of value has come to you from the borrower or anyone else on the transaction.\n\nThis is ordinary wholesale — it is why reviewing for completeness is your job rather than ours.', visualIcon: 'lock' },
    ],
  },
  {
    // 🚨 CUT FROM FOUR SLIDES TO ONE, BECAUSE THE FOUR TAUGHT THE WRONG THING.
    // They built to "once you have the sixth item, send the file within one
    // business day" — which reads as WAIT UNTIL YOU HAVE A PROPERTY. That is the
    // opposite of how this works: submit as soon as the application has been
    // TAKEN — "complete" said nothing, and implied a completeness gate the
    // form does not have (24 fields, three of them required),
    // we get the borrower to approval, and the borrower picks a home in the app
    // exactly as a self-registered or realtor-referred borrower does.
    //
    // ── 🚨 THE TWO DATES ARE GONE, AND SO IS THE CLAUSE THAT ASKED FOR THEM ──
    //
    // This module taught §2.3's record-and-transmit obligation: the date the
    // broker took the application, and whether a property address existed then.
    // Wholesale Broker Agreement v2.4 removed that obligation, so teaching it
    // would ask partners for something the form no longer accepts and the
    // contract no longer requires.
    //
    // ⚠️ THE AMENDMENT WAS ADVISED AGAINST AND DECIDED BY JESSE — the reasoning
    // is recorded in constants/partnerAgreementVersion.js under 2.4.0. This file
    // follows the document rather than carrying the older teaching.
    //
    // 🔑 WHAT SURVIVES IS EVERYTHING A BROKER ACTUALLY NEEDS FROM IT: send the
    // file as soon as the application is taken, no property address needed, no
    // waiting for a house — and the disclosures are Doorly's to prepare and
    // deliver, not theirs. §2.3's one-business-day rule remains a CEILING on a
    // file that is ready, never a reason to hold one back, which is why the
    // heading is an instruction to send rather than a deadline.
    id: 'application_dates', iconName: 'clock', title: 'When To Send The File',
    color: '#111827', textColor: '#ffffff', duration: '1 min',
    slides: [
      { heading: 'Send the file as soon as you have taken the application', body: 'You do not need a property address, and you do not need to wait for your borrower to find a house. Submit, we get them to approval, and they choose a home in their own Doorly account from there — the same way every other Doorly borrower does.\n\nDoorly does not take the subject property from you at all. Your borrower selects their home in their own account, and the property attaches to the loan there.\n\nWe prepare and deliver the Loan Estimate and the Closing Disclosure — that part is ours, not yours.', visualIcon: 'arrow' },
    ],
  },
  {
    id: 'credit_reissue', iconName: 'search', title: 'Credit Reports',
    color: '#ff082f', textColor: '#ffffff', duration: '2 min',
    slides: [
      { heading: 'Reissue your report — do not forward it', body: 'If you have already pulled credit, you can have your credit agency reissue that report to us, which saves your borrower a second pull.\n\nWhat does not work is sending us a copy yourself. The reissue has to come from the agency — a PDF you forward is not a reissued report, however current it is.', visualIcon: 'arrow' },
      // ⚠️ THE "why the agency name" SLIDE THAT FOLLOWED THIS IS GONE and its
      // point moved inline. It was a paragraph of adverse-action reasoning where
      // a clause would do, and the broker's action is the same either way: send
      // the agency name.
      { heading: 'Three things to get right', body: '• The borrower\'s written authorization has to allow disclosure to Doorly\n• The reissue has to come through the agency, not as a copy from you\n• Tell us which agency furnished it and the date it was pulled\n\nThe agency name is not admin. If we ever have to decline on something in a report, the notice has to name the agency that produced it — so a report we cannot attribute is one we could never decline on, and a file we can only approve is worse for your borrower than one we turn down early.\n\nThe first one is not yours to collect. Once you submit the file, everything you sent is already filled into your borrower\'s own Doorly application \u2014 they read it there, correct anything wrong, and sign the authorization on the last step. We put the form in front of them, they sign it there, and you do not chase it. The form names the reissue explicitly, which is what makes a reissued report usable.\n\nYou can see where it stands on My Borrowers. A file sitting at \u201cConsent \u2014 not signed yet\u201d is waiting on your borrower to finish their application, not on paperwork from you.\n\nMiss any of the three and we may not be able to rely on the report, which means a fresh pull for your borrower.', visualIcon: 'check-circle' },
      { heading: 'And it needs to be current', body: 'Credit has to be dated within 120 days of closing, measured from when the bureau was queried rather than when we received it. A report pulled in March and reissued in September is six months old.', visualIcon: 'chart' },
    ],
  },
  {
    id: 'compensation', iconName: 'currency', title: 'How You Get Paid',
    color: '#fffd5b', textColor: '#1a1a1a', duration: '2 min',
    slides: [
      // ⚠️ THE BROKERAGE IS PAID, NOT THE INDIVIDUAL — and v2.1 §4.1 says it in
      // terms. A partner who learns that at payout time has been surprised by
      // something that was always true.
      { heading: 'We pay your company', body: 'Compensation goes to the brokerage as an entity. What your company passes on to you is between you and them — we are not part of that arrangement.\n\nThe rate, the basis and the timing are set out in your agreement.', visualIcon: 'currency' },
      { heading: 'It does not move with the loan', body: 'Your compensation does not vary with the interest rate, the loan terms, or anything that stands in for a loan term. It is a fixed percentage of the amount of credit extended, and nothing we or you do to the pricing changes it.\n\nThat is a federal requirement rather than a Doorly policy, and it is there to protect you.', visualIcon: 'check-circle' },
      { heading: 'What needs to be true to get paid', body: '• You performed the origination services\n• You submitted the file before the borrower came to us on their own\n• The transaction closed\n• Your agreement is signed and current\n• We have your W-9\n• Your licensing was active at application and at closing', visualIcon: 'document' },
      { heading: 'If we might already know them, just ask', body: 'No fee is payable if the borrower was already in our system, had already started an application, or if we had documented contact with them in the previous twelve months.\n\nYou have no way of seeing that from your side, so if there is any chance they have spoken to us before — ask first. It takes a message, and it is the only condition on the list you cannot check yourself.', visualIcon: 'search' },
      { heading: 'Nothing is owed on a file that does not close', body: 'The fee arises on a closing. A borrower who applies, or is approved, and does not close does not generate one — however far the file got.', visualIcon: 'chart' },
    ],
  },
  {
    id: 'compliance', iconName: 'lock', title: 'Where Your Role Stops',
    color: '#3a6ad9', textColor: '#ffffff', duration: '3 min',
    slides: [
      // 🚨 THE ONE THAT REPLACED THE OLD "do not originate" SLIDE, AND IT IS THE
      // OPPOSITE INSTRUCTION. You may originate. You may not be Doorly.
      { heading: 'You work for your borrower, not for us', body: 'You originate for your borrower under your own license. You are not our agent, you do not represent us, and you cannot commit us to anything.\n\nThis is the most important line in your agreement, and it is easy to blur in conversation. "I work with Doorly" is fine. "I work for Doorly" is not — and it is not a wording preference, because the whole arrangement depends on which one is true.', visualIcon: 'warning' },
      { heading: 'Leave the credit decision to us', body: 'Underwriting, pricing and the decision are ours. Please do not make credit calls, determine eligibility, or tell a borrower they are approved before we have issued our decision.\n\nSaying you are submitting a file you think fits is exactly right. Saying what the answer will be is the part to hold back.', visualIcon: 'document' },
      { heading: 'Leave the terms to us too', body: 'Do not quote, negotiate or promise interest rates, pricing, loan terms or approval outcomes on a Doorly file. We set them and provide them to the borrower directly, so anything quoted earlier can only turn into a disappointment.', visualIcon: 'document' },
      { heading: 'Never take a fee from the borrower', body: 'You may not collect any fee, deposit or other payment from the borrower in connection with the AFM Program or your agreement. Your compensation comes from us and nowhere else.\n\nThis is a RESPA matter, and it is one of the things you confirm on every file you submit.', visualIcon: 'warning' },
      { heading: 'Look after their information', body: 'You hold your borrower\'s nonpublic personal information. Keep sensible safeguards on it, use it only for this transaction, and tell us promptly if you think it has been seen or shared without authorization.', visualIcon: 'lock' },
      { heading: 'You\'re ready', body: 'You now know what you perform, what you are confirming on each file, the two dates to send, how credit reissue works, how your company gets paid, and where your role stops.\n\nStart sending files — and keep hold of the one that underpins all of it: you originate in your own capacity, for your borrower.', visualIcon: 'check-circle' },
    ],
  },
];

// 🚨 THE HOMEBUILDER DECK IS THE ONLY PLACE THESE TERMS EVER REACH A BUILDER, AND
// THAT IS WHY THE COMPLIANCE MODULE BELOW IS NOT OPTIONAL.
//
// ⚠️ A BUILDER SIGNS NOTHING. Realtors sign the Realtor Services Agreement and
// loan officers the Wholesale Broker Agreement, and for both of them this deck
// is the SECOND place they meet these rules — the agreement is the first and the
// authoritative one. Homebuilders are not paid, so there is deliberately no
// homebuilder agreement (see backend/constants/partnerAgreements.js:
// AGREEMENT_FOR_TYPE has no entry, and absence means no agreement rather than a
// default one). There is no first place. This deck is it.
//
// ⚠️ SO DO NOT TRIM THIS MODULE AS DUPLICATIVE. It looks like a copy of the
// realtor compliance module, and the obvious tidy-up is to say "the agreement
// already covers this" — which is true for the other two decks and false for
// this one. Deleting it does not remove a redundancy, it removes the only
// notice a builder ever gets.
//
// ⚠️ AND THE LAW DOES NOT CARE WHETHER A CONTRACT EXISTS. RESPA §8 prohibits the
// fee regardless of what anyone signed; taking a loan application without a
// license is unlicensed originator activity whether or not a builder agreed not
// to do it. A builder's on-site sales agent is at least as likely to do both as
// a realtor is, and is the only one of the three who was never told not to.
const HOMEBUILDER_TRAINING_MODULES = [
  {
    id: 'what_is_doorly_hb', iconName: 'home', title: 'What is Doorly?', color: '#3a6ad9', textColor: '#ffffff', duration: '2 min',
    slides: [
      { heading: 'Doorly helps your buyers get financed', body: 'Doorly is a full-stack homebuying platform that takes buyers from application to keys — all in one place. When your buyer can\'t get traditional financing, Doorly steps in with a structured mortgage solution built for them.', visualIcon: 'home' },
      { heading: 'How Doorly works', body: '1. Your buyer applies and gets approved through Doorly\n2. The buyer selects a home from your community\n3. Doorly purchases the home with a cash-backed offer and simultaneously resells it to the buyer through Doorly\'s Ability-First Mortgage', visualIcon: 'arrow' },
      { heading: 'Why builders partner with Doorly', body: 'When your buyers can\'t qualify for traditional financing, deals fall apart. Doorly gives those buyers a real path to closing — so your homes get sold and your buyers get their keys.', visualIcon: 'users' },
    ],
  },
  {
    id: 'which_buyers_hb', iconName: 'search', title: 'Which Buyers to Refer', color: '#acf44e', textColor: '#1a1a1a', duration: '2 min',
    slides: [
      { heading: 'Who is the right fit for Doorly?', body: 'Doorly is designed for buyers who are close to qualifying — but don\'t fit traditional lending guidelines. This includes self-employed buyers, credit-challenged buyers, and anyone with non-traditional income or documentation challenges.', visualIcon: 'user' },
      { heading: 'Green flags to look for', body: 'Stable income (including self-employed or non-traditional)\nHas funds for down payment and closing costs\nMotivated to buy — not renting or investing\nInterested in homes in your community', visualIcon: 'check-circle' },
      { heading: 'Red flags to avoid', body: 'No verifiable income source\nActive bankruptcy or unresolved major credit issues\nInsufficient funds to close\nNon-owner-occupied / investment intent', visualIcon: 'warning' },
    ],
  },
  {
    id: 'track_buyers_hb', iconName: 'chart', title: 'Track Your Buyers', color: '#3a6ad9', textColor: '#ffffff', duration: '2 min',
    slides: [
      { heading: 'Full visibility from application to keys', body: 'Once you submit a buyer through your DoorlyPro dashboard, you\'ll have real-time visibility into their progress — from application to approval, home selection, and closing.', visualIcon: 'chart' },
      { heading: 'Milestone updates — no chasing required', body: 'You\'ll receive email notifications at every key milestone: when your buyer is approved, when they\'re actively shopping, when an offer is accepted, and when they close.', visualIcon: 'bell' },
      { heading: 'What you see, and what you don\'t', body: 'You see where your buyer is in the process. You do not see their income, credit, documents, or the dollar amounts in their file — nobody outside Doorly does. If a buyer stops moving forward, you\'ll be told that they have, not why. Ask the buyer, not Doorly.', visualIcon: 'lock' },
      // ⚠️ BUILDERS GET THIS TOO — they do home selection inside their own
      // community, which is the whole reason the disclosure exists. Worded the
      // same way as the realtor deck on purpose: one feature, one explanation.
      { heading: 'The one exception — their shopping range', body: 'On their application your buyer is asked whether Doorly may tell you the maximum home price they are approved for, rounded down. If they say yes, it shows on your dashboard as "Up to $X" once they are approved, so you know which homes in your community to show them.\n\nIf it says "Range not shared", they either have not answered yet or chose not to — the tooltip says which. It is their call and they can change it any time; Doorly cannot override it.', visualIcon: 'currency' },
    ],
  },
  {
    // 🚨 THE MODULE A BUILDER COULD OTHERWISE BREACH WITHOUT EVER BEING TOLD.
    // See the block comment above this array for why it exists and must not be
    // trimmed: there is no homebuilder agreement, so this is the only notice.
    id: 'compliance_hb', iconName: 'lock', title: 'Where Your Role Stops', color: '#111827', textColor: '#ffffff', duration: '3 min',
    slides: [
      { heading: 'Never collect a fee from the buyer', body: 'You may not charge or collect any fee, deposit or other payment from a buyer for introducing them to Doorly, or in connection with their financing. Not a referral fee, not a processing fee, not a credit against the price of the home.\n\nThis is federal law — RESPA — and it applies whether or not anything is signed. It is the one rule on this slide with its own penalties.', visualIcon: 'warning' },
      { heading: 'Do not take the loan application', body: 'Your buyer creates their own Doorly account and completes their own application. You may not fill in any part of it for them, and you may not collect, hold or pass on their income, asset or credit documents.\n\nOffering to "save them a step" by taking their pay stubs or their income over the phone is the most common way this goes wrong, and it is loan-originator work you are not licensed for.', visualIcon: 'warning' },
      { heading: 'Do not quote rates or terms', body: 'Do not quote or estimate an interest rate, a monthly payment, a down payment requirement or a loan term, and never tell a buyer they will be approved. You will be asked — buyers ask the person in front of them.\n\nThe answer is that Doorly provides all terms directly, and then you send them to Doorly. A number you offer to be helpful becomes a number they relied on.', visualIcon: 'warning' },
      { heading: 'You\'re ready', body: 'You now understand how Doorly works, which buyers to submit, how to track their progress, and where your role stops. Start submitting buyers and help more people move into your homes.', visualIcon: 'check-circle' },
    ],
  },
];

const HOMEBUILDER_STEPS = [
  { key: 'email_verified', label: 'Builder Profile', num: 1 },
  { key: 'training_pending', label: 'Training', num: 2 },
  { key: 'active', label: 'Complete', num: 3 },
];


// ─────────────────────────────────────────────────────────────────────────────
// THE TRAINING DECK, AS A COMPONENT BOTH SURFACES MOUNT.
//
// 🚨 "Start Training" SENT A PARTNER BACK TO THE REGISTRATION ROUTE. The task's
// destination was /doorlypro/onboarding — which since the profile step was
// removed is nothing BUT this deck, so the page was right and the journey was
// wrong: a partner already on their dashboard was navigated out of it to a URL
// that reads as sign-up, and returned by a redirect when they finished.
//
// 🔑 SO THE DECK LEFT THE PAGE RATHER THAN THE PAGE LEARNING A NEW TRICK. It is
// exported from here because the module decks it renders are defined in this file
// and moving those too would be a much larger change for no gain. The onboarding
// route mounts it as its training step; the dashboard mounts it in a modal.
//
// ⚠️ PROGRESS IS THE SAME localStorage KEY EITHER WAY, keyed on the partner id, so
// somebody who starts in the modal and finishes on the route does not begin again.
// ⚠️ AND COMPLETION IS THE SERVER'S, NOT THE MODAL'S. onDone fires after
// POST /me/training succeeds; a modal that closed on its own would leave a
// partner believing they had finished something the server never recorded.
// ─────────────────────────────────────────────────────────────────────────────
// 🔑 WHICH DECK THIS PERSON GETS, DERIVED ONCE. The dashboard needs the same
// answer the onboarding page computes, and a second copy of "broker gets the firm
// module prepended" is how one surface starts showing four modules and the other
// five.
export function modulesForPartner({ partnerType, isBroker }) {
  const typeModules = partnerType === 'homebuilder' ? HOMEBUILDER_TRAINING_MODULES
    : partnerType === 'loan_officer' ? LOAN_OFFICER_TRAINING_MODULES
      : REALTOR_TRAINING_MODULES;
  return isBroker ? [ACCOUNT_LEVEL_MODULE, ...typeModules] : typeModules;
}

// 🔑 RE-EXPORTED from utils/trainingProgress.js, where it is testable. The
// dashboard imports the pair from here alongside the deck it counts, so the
// two always come from one place.
export { trainingProgress, trainingProgressKey };

export function PartnerTraining({ partner, modules, onDone, onExit }) {
  const [activeModule, setActiveModule] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const activeModules = modules;
  const trainingKey = trainingProgressKey(partner?.id);
  const [completedModules, setCompletedModules] = useState(() => {
    if (!trainingKey) return new Set();
    try { return new Set(JSON.parse(localStorage.getItem(trainingKey) || '[]')); } catch { return new Set(); }
  });
  const allTrainingComplete = completedModules.size === activeModules.length;
  const authHeaders = { Authorization: `Bearer ${localStorage.getItem('doorlypro_token')}` };

  const handleTraining = async () => {
    if (!allTrainingComplete) {
      setError('Please complete all training modules before continuing.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/doorlypro/me/training`, {}, { headers: authHeaders });
      if (onDone) onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete training.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <style>{`
              @keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
              @keyframes bounceIn { 0% { transform: scale(0.5); opacity: 0; } 60% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
              @keyframes pulse { 0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(172,244,78,0.4); } 50% { transform: scale(1.02); box-shadow: 0 0 0 8px rgba(172,244,78,0); } }
            `}</style>

            {activeModule === null ? (
            /* Module Selection Screen */
            <div>
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <div style={{ marginBottom: '12px' }}><Icon name="check-circle" size={48} color="#3a6ad9" /></div>
                <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '800', color: '#111827' }}>DoorlyPro Training</h2>
                <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Complete all 5 modules to activate your account</p>
                <div style={{ margin: '20px auto 0', maxWidth: '300px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Progress</span>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#111827' }}>{completedModules.size}/{activeModules.length}</span>
                  </div>
                  <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(completedModules.size / activeModules.length) * 100}%`, background: '#acf44e', borderRadius: '4px', transition: 'width 0.5s ease' }} />
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                {activeModules.map((mod, idx) => {
                  const done = completedModules.has(mod.id);
                  const locked = idx > 0 && !completedModules.has(activeModules[idx - 1].id);
                  return (
                    <div key={mod.id} onClick={() => { if (!locked) { setActiveModule(mod); setCurrentSlide(0); } }}
                      style={{ padding: '18px', borderRadius: '14px', border: `2px solid ${done ? '#acf44e' : locked ? '#e5e7eb' : mod.color}`, background: done ? '#f0fdf4' : locked ? '#f9fafb' : '#fff', cursor: locked ? 'not-allowed' : 'pointer', position: 'relative', opacity: locked ? 0.5 : 1, transition: 'all 0.2s' }}>
                      {done && <div style={{ position: 'absolute', top: '8px', right: '8px', width: '20px', height: '20px', borderRadius: '50%', background: '#acf44e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="check" size={12} color="#1a1a1a" /></div>}
                      {locked && <div style={{ position: 'absolute', top: '8px', right: '8px' }}><Icon name="lock" size={14} color="#9ca3af" /></div>}
                      <div style={{ marginBottom: '6px' }}><Icon name={mod.iconName} size={28} color={done ? '#acf44e' : mod.color} /></div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827', marginBottom: '2px' }}>{mod.title}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>{mod.duration}</div>
                      {done && <div style={{ fontSize: '11px', color: '#2d6a0a', fontWeight: '600', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}><Icon name="check" size={10} color="#acf44e" /> Completed</div>}
                    </div>
                  );
                })}
              </div>

              {/* ── 🚨 THE BUTTON NO LONGER PROMISES ACTIVATION ───────────────
                  It read "Training Complete — Activate Account", and finishing
                  the deck WAS the last gate when that was written. It is not
                  any more: activation needs the agreement signed, the training
                  done AND Doorly's licence verification
                  (constants/partnerActivationGate). A partner who pressed this
                  and did not become active was told by the button that they
                  would — so the next screen reads as a failure rather than as
                  the next step.

                  🔑 IT NAMES WHAT IT ACTUALLY DOES: records the training and
                  returns them to the dashboard, where whatever is left is
                  listed. Claiming less than it delivers is survivable; claiming
                  more is not. */}
              {allTrainingComplete && (
                <button onClick={handleTraining} disabled={submitting}
                  style={{ width: '100%', padding: '16px', background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '800', fontSize: '16px', animation: 'pulse 2s infinite' }}>
                  {submitting ? 'Saving…' : 'Training Complete — Go to Dashboard'}
                </button>
              )}
            </div>
            ) : (
            /* Slide Viewer */
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <button onClick={() => setActiveModule(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px', padding: '4px' }}>←</button>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>Module {activeModules.findIndex(m => m.id === activeModule.id) + 1} of {activeModules.length}</div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center', gap: '6px' }}><Icon name={activeModule.iconName} size={16} color={activeModule.color} /> {activeModule.title}</div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {activeModule.slides.map((_, i) => (
                    <div key={i} style={{ width: i === currentSlide ? '18px' : '6px', height: '6px', borderRadius: '3px', background: i === currentSlide ? activeModule.color : '#e5e7eb', transition: 'all 0.3s' }} />
                  ))}
                </div>
              </div>

              <div key={`${activeModule.id}-${currentSlide}`}
                style={{ background: `linear-gradient(135deg, ${activeModule.color}15, ${activeModule.color}05)`, border: `2px solid ${activeModule.color}40`, borderRadius: '16px', padding: '28px', marginBottom: '24px', minHeight: '220px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', animation: 'slideIn 0.3s ease' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px', animation: 'bounceIn 0.4s ease', lineHeight: 1 }}>
                  <Icon name={activeModule.slides[currentSlide].visualIcon || 'document'} size={48} color={activeModule.color} />
                </div>
                <h3 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: '800', color: '#111827', lineHeight: '1.3' }}>
                  {activeModule.slides[currentSlide].heading}
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#374151', lineHeight: '1.7', whiteSpace: 'pre-line', maxWidth: '380px' }}>
                  {activeModule.slides[currentSlide].body}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                {currentSlide > 0 && (
                  <button onClick={() => setCurrentSlide(p => p - 1)}
                    style={{ flex: 1, padding: '13px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                    ← Back
                  </button>
                )}
                {currentSlide < activeModule.slides.length - 1 ? (
                  <button onClick={() => setCurrentSlide(p => p + 1)}
                    style={{ flex: 2, padding: '13px', background: activeModule.color, color: activeModule.textColor, border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '800', fontSize: '14px' }}>
                    Next →
                  </button>
                ) : (
                  <button onClick={() => {
                    const updated = new Set([...completedModules, activeModule.id]);
                    setCompletedModules(updated);
                    if (trainingKey) localStorage.setItem(trainingKey, JSON.stringify([...updated]));
                    setTimeout(() => setActiveModule(null), 300);
                  }} style={{ flex: 2, padding: '13px', background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '800', fontSize: '14px' }}>
                    Complete Module
                  </button>
                )}
              </div>
              <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '12px', color: '#9ca3af' }}>
                {currentSlide + 1} of {activeModule.slides.length}
              </div>
            </div>
            )}

            {!allTrainingComplete && activeModule === null && (
              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, textAlign: 'center' }}>
                Complete all modules to activate your account.
              </p>
            )}
          </div>
      {error && <p style={{ marginTop: 12, fontSize: 13, color: '#ff082f' }}>{error}</p>}
      {onExit && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button type="button" onClick={onExit}
            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
            Close — your progress is saved
          </button>
        </div>
      )}
    </>
  );
}

// 🔑 BEHIND THE SIGN-IN CHECK, LIKE EVERY OTHER SIGNED-IN DoorlyPro PAGE (Hannah, 2026-11-02). RequirePartnerAuth
// holds the render until the session is read and sends a signed-out visitor to /doorlypro/login?next=<this page>.
export default function DoorlyProOnboarding() {
  return (
    <RequirePartnerAuth>
      <DoorlyProOnboardingPage />
    </RequirePartnerAuth>
  );
}

function DoorlyProOnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, partner } = useDoorlyProAuth();
  const isLoanOfficer = partner?.partner_type === 'loan_officer';
  const isHomebuilder = partner?.partner_type === 'homebuilder';
  // ⚠️ THE ROLE COMES FROM THE SERVER, NOT THE TOKEN — the same rule the nav
  // follows. It starts false so a partner never briefly sees the firm module
  // before the check answers.
  const [isBroker, setIsBroker] = useState(false);
  useEffect(() => {
    const t = localStorage.getItem('doorlypro_token');
    if (!t) return;
    let live = true;
    fetch(`${API_URL}/api/doorlypro/firm/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (live) setIsBroker(!!(d && d.isBroker)); })
      .catch(() => { if (live) setIsBroker(false); });
    return () => { live = false; };
  }, []);
  // ⚠️ THREE TYPES, THREE DECKS. This was a two-way branch — homebuilder or
  // everyone-else — so a loan officer completed a deck written for realtors and
  // was told their role was to send buyers, with compensation copy that could
  // only describe one basis.
  //
  // ⚠️ AN UNSET OR UNRECOGNISED TYPE FALLS TO THE REALTOR DECK, which is the most
  // RESTRICTIVE of the three: it forbids taking an application outright rather
  // than conditionally, and it names no compensation basis. Over-restricting a
  // partner whose type has not been set is recoverable; under-restricting them is
  // telling someone they may do something their agreement forbids. They cannot
  // sign an agreement without a type anyway — the signing route 403s — so this is
  // what they read while Partner Success sets it.
  // ⚠️ PREPENDED TO WHICHEVER TYPE DECK APPLIES. The account-level holder gets the
  // firm module AND the product content for their type — a mortgage broker still
  // needs to know who qualifies. allTrainingComplete counts activeModules, so the
  // extra module is automatically part of completion rather than needing its own
  // bookkeeping.
  const typeModules = isHomebuilder ? HOMEBUILDER_TRAINING_MODULES
    : isLoanOfficer ? LOAN_OFFICER_TRAINING_MODULES
      : REALTOR_TRAINING_MODULES;
  const activeModules = isBroker ? [ACCOUNT_LEVEL_MODULE, ...typeModules] : typeModules;
  const activeSteps = isHomebuilder ? HOMEBUILDER_STEPS : STEPS;
  const [currentStatus, setCurrentStatus] = useState(null);
  // 🔑 THE SHARED READINESS (push C) — what is actually left.
  const [readiness, setReadiness] = useState(null);
  const [agreementSigned, setAgreementSigned] = useState(false);
  const [agreementSignedAt, setAgreementSignedAt] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // The open "is this your firm?" question, if the profile save raised one.
  const [accountMatch, setAccountMatch] = useState(null);

  // ⚠️ BOTH ANSWERS GO TO THE SAME ENDPOINT, which is what makes "no" record a
  // DECLINE rather than just create an account. Without the decline row, a
  // partner who says no and later re-saves the same company name is asked again,
  // and the only escape is typing a company name that is not theirs.
  const answerFirmMatch = async (accountId) => {
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/doorlypro/me/account-match`,
        accountId ? { account_id: accountId }
                  : { decline: true, account_id: (accountMatch.candidates[0] || {}).id },
        { headers: authHeaders });
      setAccountMatch(null);
      setCurrentStatus('training_pending');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save that. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Step 1 profile fields
  const [profile, setProfile] = useState({
    brokerage: '',
    license_number: '',
    license_state: '',
    years_experience: '',
    builder_company_name: '',
    builder_markets: '',
  });

  // ACH / Banking
  const [achForm, setAchForm] = useState({ ach_bank_name: '', ach_account_holder_name: '', ach_routing_number: '', ach_account_number: '', ach_account_type: 'checking' });
  const [achSubmitting, setAchSubmitting] = useState(false);
  const [achError, setAchError] = useState(null);

  // ⚠️ THE TRAINING STATE LIVES IN <PartnerTraining> NOW, not here. Slide
  // position, which modules are done, the localStorage key they are cached under
  // and the completion POST all moved with the deck when the dashboard began
  // mounting it too — leaving copies here would be a second set of the same
  // bookkeeping, silently diverging from the one actually driving the screen.

  const authHeaders = { Authorization: `Bearer ${token}` };

  // ── 🚨 NO TOKEN MEANT NO REDIRECT, AND THAT IS THE BROKEN LINK ────────────
  //
  // A partner reported that the link in "The tool that answers 'will this buyer
  // qualify?'" does not work. Every CTA in the activation sequence points at
  // this page, and every effect on it opens `if (!token) return;` — so a partner
  // clicking an email link in a browser with no DoorlyPro session fetched
  // nothing, navigated nowhere, and got a step list with no step active. Nothing
  // errored. The page simply had no content to render, which from their side is
  // a link that does not work.
  //
  // 🔑 AND IT IS EVERY EMAIL IN THE SEQUENCE, not that one. Five steps share one
  // resumeUrl, so this was broken for all of them, for anybody not already
  // signed in on that device — which is most people opening a marketing email.
  //
  // ⚠️ WITH A RETURN PATH, OR THE FIX TRADES ONE DEAD END FOR ANOTHER. Landing
  // them on a login screen that then sends them to the dashboard loses the thing
  // they clicked. `next` carries it and DoorlyProLogin honours it.
  useEffect(() => {
    if (token) return;
    const next = `${location.pathname}${location.search || ''}`;
    navigate(`/doorlypro/login?next=${encodeURIComponent(next)}`, { replace: true });
  }, [token, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/doorlypro/onboarding`, { headers: authHeaders })
      .then((res) => {
        const status = res.data.onboarding_status;
        if (status === 'active') {
          navigate('/doorlypro/dashboard', { replace: true });
        } else {
          // Map 'onboarding' (new registrations) to 'email_verified' (profile setup step)
          setCurrentStatus(status === 'onboarding' ? 'email_verified' : status);
          setReadiness(res.data.readiness || null);
          setAgreementSigned(!!res.data.checklist?.agreement_signed);
          setAgreementSignedAt(res.data.agreement_signed_at || null);
        }
      })
      .catch(() => setError('Failed to load onboarding status.'))
      .finally(() => setLoadingStatus(false));
  }, [token, location]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProfileChange = (e) => {
    setProfile((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (isHomebuilder) {
      if (!profile.builder_company_name) { setError('Builder / Company Name is required.'); return; }
    } else {
      if (!profile.brokerage || !profile.license_number || !profile.license_state) { setError('Please fill in all required fields.'); return; }
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.put(`${API_URL}/api/doorlypro/me/profile`, profile, { headers: authHeaders });
      // 🚨 A FIRM QUESTION STOPS HERE RATHER THAN BEING CARRIED FORWARD. The
      // profile SAVED — that is done — but the partner is not attached to an
      // account until they answer, and the whole point of the proposal is that a
      // human answers it while they are still in front of it. Advancing to
      // training and surfacing this later would mean asking someone about a
      // company name they typed days ago.
      if (res.data && res.data.accountMatch) {
        setAccountMatch(res.data.accountMatch);
        return;
      }
      // Agreement + banking are now post-onboarding Dashboard tasks, so every
      // partner type goes straight from profile to training.
      setCurrentStatus('training_pending');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save profile.');
    } finally {
      setSubmitting(false);
    }
  };


  if (loadingStatus) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div style={{ textAlign: 'center', color: '#6b7280' }}>
          <svg className="animate-spin mx-auto mb-3" width="32" height="32" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="#3a6ad9" strokeWidth="4" />
            <path className="opacity-75" fill="#3a6ad9" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p style={{ fontSize: 14 }}>Loading...</p>
        </div>
      </div>
    );
  }

  // 🚨 THE FIRM QUESTION TAKES THE WHOLE SCREEN, and takes it BEFORE the step
  // chrome. Rendered as a banner above the profile form it would sit next to the
  // company-name field that raised it, reading as a validation warning somebody
  // can scroll past — and scrolling past it means continuing with no account.
  // There is exactly one thing to do here, so there is exactly one thing on screen.
  if (accountMatch) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-black mb-1">One thing before we continue</h1>
            <p className="text-gray-500 text-sm">Your details are saved. We just need to know which firm you’re with.</p>
          </div>
          <FirmMatchPrompt
            question={accountMatch.question}
            companyName={accountMatch.companyName}
            candidates={accountMatch.candidates}
            registry={accountMatch.registry}
            busy={submitting}
            onJoin={(id) => answerFirmMatch(id)}
            onCreate={() => answerFirmMatch(null)}
          />
          {error && (
            <p style={{ marginTop: 14, fontSize: 13, color: '#ff082f' }}>{error}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/doorlypro/login" className="inline-block">
            <img
              src={`${process.env.PUBLIC_URL}/assets/DoorlyPro.png`}
              alt="DoorlyPro"
              style={{ height: 40, width: 'auto' }}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline'; }}
            />
            <span style={{ display: 'none', fontSize: 24, fontWeight: 800, color: '#3a6ad9' }}>Doorly<span style={{ color: '#111827' }}>Pro</span></span>
          </Link>
          <h1 className="text-2xl font-bold text-black mt-6 mb-1">Partner Onboarding</h1>
          <p className="text-gray-500 text-sm">Complete these steps to activate your partner account.</p>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 28 }}>
          {activeSteps.filter((s) => s.key !== 'active').map((step, idx) => {
            const isActive = step.key === currentStatus;
            const isDone = activeSteps.findIndex((s) => s.key === currentStatus) > idx;
            return (
              <React.Fragment key={step.key}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 700,
                      backgroundColor: isDone ? '#acf44e' : isActive ? '#3a6ad9' : '#e5e7eb',
                      color: isDone || isActive ? '#fff' : '#6b7280',
                    }}
                  >
                    {isDone ? (
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : step.num}
                  </div>
                  <span style={{ fontSize: 11, color: isActive ? '#3a6ad9' : isDone ? '#acf44e' : '#9ca3af', fontWeight: isActive ? 600 : 400 }}>
                    {step.label}
                  </span>
                </div>
                {idx < activeSteps.filter(s => s.key !== 'active').length - 1 && (
                  <div style={{ width: 32, height: 2, backgroundColor: isDone ? '#acf44e' : '#e5e7eb', marginBottom: 18 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {error && (
          <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Step 1: Profile Setup */}
        {currentStatus === 'email_verified' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <h2 className="text-lg font-bold text-gray-900 mb-1">
              {isHomebuilder ? 'Step 1: Your Builder Profile' : 'Step 1: Profile Setup'}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {isHomebuilder ? 'Tell us about your building company.' : isLoanOfficer ? 'Tell us about your lending business.' : 'Tell us about your professional background.'}
            </p>
            <form onSubmit={handleProfileSubmit} noValidate>
              {isHomebuilder ? (
              <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Builder / Company Name *</label>
                <input name="builder_company_name" type="text" value={profile.builder_company_name} onChange={handleProfileChange}
                  placeholder="e.g. Cornerstone Homes" style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Markets / Communities <span className="text-xs text-gray-400 font-normal ml-1">Where do you build?</span></label>
                <input name="builder_markets" type="text" value={profile.builder_markets} onChange={handleProfileChange}
                  placeholder="e.g. Austin TX, DFW TX, Houston TX" style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
              </div>
              </>
              ) : (
              <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {isLoanOfficer ? 'Mortgage Brokerage / IMB Name *' : 'Brokerage Name *'}
                </label>
                <input name="brokerage" type="text" value={profile.brokerage} onChange={handleProfileChange}
                  placeholder={isLoanOfficer ? 'e.g. Movement Mortgage' : 'ABC Realty Group'}
                  style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {isLoanOfficer ? 'NMLS Number *' : 'License Number *'}
                </label>
                <input name="license_number" type="text" value={profile.license_number} onChange={handleProfileChange}
                  placeholder={isLoanOfficer ? 'NMLS#' : 'TX-12345678'}
                  style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">License State *</label>
                <input name="license_state" type="text" value={profile.license_state} onChange={handleProfileChange} placeholder="TX" maxLength={2} style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Years of Experience</label>
                <input name="years_experience" type="number" min="0" max="60" value={profile.years_experience} onChange={handleProfileChange}
                  placeholder={isLoanOfficer ? '8' : '5'}
                  style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
              </div>
              </>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary btn-full text-white hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: '#3a6ad9' }}
              >
                {submitting ? 'Saving...' : (
                  <>
                    <span>Continue</span>
                    <span className="btn-arrow">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
                    </span>
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Agreement */}
        {currentStatus === 'agreement_pending' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Step 2: Partner Agreement</h2>
            {agreementSigned ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <Icon name="check-circle" size={18} color="#acf44e" />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#166534' }}>Agreement Signed</span>
                  {agreementSignedAt && (
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>
                      on {new Date(agreementSignedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                  )}
                </div>
                <Link
                  to="/doorlypro/onboarding/agreement"
                  style={{ fontSize: 13, color: '#3a6ad9', fontWeight: 600, textDecoration: 'underline', display: 'block', marginBottom: 16 }}
                >
                  View Agreement
                </Link>
                <button
                  onClick={() => setCurrentStatus('banking_pending')}
                  className="btn-primary btn-full text-white hover:opacity-90"
                  style={{ backgroundColor: '#3a6ad9' }}
                >
                  Continue →
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-5">Review and e-sign the partner agreement to continue.</p>
                <button
                  onClick={() => navigate('/doorlypro/onboarding/agreement')}
                  className="btn-primary btn-full text-white hover:opacity-90"
                  style={{ backgroundColor: '#3a6ad9' }}
                >
                  Review & Sign Agreement →
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 3: Banking / ACH — Realtors and LOs only */}
        {currentStatus === 'banking_pending' && !isHomebuilder && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: '#edf1fb', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: '2px solid #c5d3f5' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3a6ad9" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '800', color: '#111827' }}>Broker Banking Information</h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
                Please provide your Company's Banking Information to receive payments. You can also add this later in "My Profile".
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '5px' }}>Account Holder Name *</label>
                <input type="text" value={achForm.ach_account_holder_name} onChange={e => setAchForm(p => ({ ...p, ach_account_holder_name: e.target.value }))}
                  placeholder="Full legal name on the bank account" style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '5px' }}>Bank Name *</label>
                <input type="text" value={achForm.ach_bank_name} onChange={e => setAchForm(p => ({ ...p, ach_bank_name: e.target.value }))}
                  placeholder="e.g. Chase, Wells Fargo" style={inputStyle} onFocus={focusBorder} onBlur={blurBorder} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '5px' }}>Account Type *</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {['checking', 'savings'].map(t => (
                    <button key={t} type="button" onClick={() => setAchForm(p => ({ ...p, ach_account_type: t }))}
                      style={{ flex: 1, padding: '12px', border: `2px solid ${achForm.ach_account_type === t ? '#3a6ad9' : '#e5e7eb'}`, borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', background: achForm.ach_account_type === t ? '#edf1fb' : '#fff', color: achForm.ach_account_type === t ? '#3a6ad9' : '#6b7280' }}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '5px' }}>Routing Number *</label>
                <input type="text" inputMode="numeric" maxLength={9} value={achForm.ach_routing_number}
                  onChange={e => setAchForm(p => ({ ...p, ach_routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                  placeholder="9-digit routing number" style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '2px' }} onFocus={focusBorder} onBlur={blurBorder} />
                {achForm.ach_routing_number && achForm.ach_routing_number.length !== 9 && achForm.ach_routing_number.length > 0 && (
                  <div style={{ fontSize: '12px', color: '#ff082f', marginTop: '4px' }}>Routing number must be exactly 9 digits</div>
                )}
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '5px' }}>Account Number *</label>
                <input type="text" inputMode="numeric" value={achForm.ach_account_number}
                  onChange={e => setAchForm(p => ({ ...p, ach_account_number: e.target.value.replace(/\D/g, '').slice(0, 17) }))}
                  placeholder="Bank account number" style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '2px' }} onFocus={focusBorder} onBlur={blurBorder} />
              </div>

              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '1px', flexShrink: 0 }}>
                  <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
                  Your banking information is encrypted and stored securely. It is used only for ACH payment processing of partner fees.
                </div>
              </div>

              {achError && (
                <div style={{ background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#ff082f', fontWeight: '600' }}>{achError}</div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button type="button" onClick={() => setCurrentStatus('agreement_pending')}
                style={{ flex: 1, padding: '13px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                Back
              </button>
              <button type="button" disabled={achSubmitting || !achForm.ach_account_holder_name?.trim() || !achForm.ach_bank_name?.trim() || achForm.ach_routing_number?.length !== 9 || !achForm.ach_account_number?.trim()}
                onClick={async () => {
                  setAchError(null); setAchSubmitting(true);
                  try {
                    const res = await axios.post(`${API_URL}/api/doorlypro/onboarding/ach`, achForm, { headers: authHeaders });
                    if (res.data.success) setCurrentStatus('training_pending');
                    else setAchError(res.data.error || 'Failed to save.');
                  } catch (e) { setAchError(e.response?.data?.error || 'Network error.'); }
                  finally { setAchSubmitting(false); }
                }}
                style={{ flex: 2, padding: '13px', background: achSubmitting || !achForm.ach_account_holder_name?.trim() || !achForm.ach_bank_name?.trim() || achForm.ach_routing_number?.length !== 9 || !achForm.ach_account_number?.trim() ? '#e5e7eb' : '#3a6ad9', color: achSubmitting || !achForm.ach_account_holder_name?.trim() || !achForm.ach_bank_name?.trim() || achForm.ach_routing_number?.length !== 9 || !achForm.ach_account_number?.trim() ? '#9ca3af' : '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '800', fontSize: '14px' }}>
                {achSubmitting ? 'Saving...' : 'Continue →'}
              </button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API_URL}/api/doorlypro/onboarding/ach/skip`, {}, { headers: authHeaders });
                    setCurrentStatus('training_pending');
                  } catch (e) { setAchError(e.response?.data?.error || 'Failed to skip.'); }
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#9ca3af', textDecoration: 'underline', padding: '8px' }}>
                Skip for now
              </button>
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#d1d5db', lineHeight: '1.6' }}>
                You can add your payment information later under{' '}
                <strong style={{ color: '#6b7280' }}>My Profile → Banking Information</strong>.
                You will not receive buyer fees until banking information is on file.
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Interactive Training */}
        {/* ⚠️ THE SAME DECK THE DASHBOARD MODAL MOUNTS, from the component above.
            Two copies of a five-module deck is how one of them stops matching the
            agreement it is supposed to track. */}
        {/* ── 🚨 FINISH SETTING UP, NOT A RE-RUN OF THE DECK (push C) ─────
            Every activation email links here. A partner in training_pending
            who had ALREADY finished training was shown the deck again, and
            finishing it sent her to the dashboard: the agreement she was
            actually missing was never named. license_pending rendered nothing
            at all. Both now get the list of what is left. */}
        {(currentStatus === 'license_pending'
          || (currentStatus === 'training_pending' && readiness
            && !(readiness.missing || []).some((m) => m.key === 'training'))) && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8" data-testid="onboarding-finish-setup">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Finish setting up</h2>
            <p className="text-sm text-gray-600 mb-4">
              {(readiness?.missing || []).length
                ? 'Here is what is left before your account is active:'
                : 'Everything on your side is done.'}
            </p>
            <ReadinessChecklist missing={readiness?.missing} notice={readiness?.notice}
              returnTo="/doorlypro/onboarding" testId="onboarding-readiness" />
            <button type="button" onClick={() => navigate('/doorlypro/dashboard')}
              className="mt-2 text-sm font-semibold" style={{ color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Go to my dashboard
            </button>
          </div>
        )}

        {currentStatus === 'training_pending'
          && !(readiness && !(readiness.missing || []).some((m) => m.key === 'training')) && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <PartnerTraining
              partner={partner}
              modules={activeModules}
              onDone={() => navigate('/doorlypro/dashboard', { replace: true })}
            />
          </div>
        )}
      </div>
    </div>
  );
}
